import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { PlusCircle, Briefcase, AlertTriangle } from 'lucide-react';
import CreateProjectDialog from '@/components/CreateProjectDialog';
import ProjectCard from '@/components/ProjectCard';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabaseClient';

const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProjects(data || []);
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to fetch projects. Please try again.");
      toast({
        title: "Error",
        description: "Could not load projects from the database.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const addProject = async (newProjectData) => {
    try {
      const { data, error } = await supabase
        .from('projects')
        .insert([newProjectData])
        .select();

      if (error) throw error;
      
      if (data && data.length > 0) {
        setProjects(prevProjects => [data[0], ...prevProjects]);
        toast({
          title: "Project Created",
          description: `"${newProjectData.name}" has been successfully created.`,
          variant: "default",
        });
        setIsDialogOpen(false);
      } else {
        throw new Error("No data returned after insert.");
      }
    } catch (err) {
      console.error("Error creating project:", err);
      toast({
        title: "Error",
        description: "Could not create the project. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-8">
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex justify-between items-center"
      >
        <h1 className="text-3xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">Projects</h1>
        <Button onClick={() => setIsDialogOpen(true)} className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white transition-all">
          <PlusCircle className="mr-2 h-5 w-5" /> Create Project
        </Button>
      </motion.div>

      {isLoading && (
        <div className="flex justify-center items-center py-10">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-4 border-t-blue-500 border-r-blue-500 border-b-slate-700 border-l-slate-700 rounded-full"
          />
        </div>
      )}

      {!isLoading && error && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-10 bg-slate-800/50 p-6 rounded-lg border border-red-500/50"
        >
          <AlertTriangle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h2 className="text-xl font-semibold text-red-300 mb-2">Error Loading Projects</h2>
          <p className="text-slate-400 mb-4">{error}</p>
          <Button onClick={fetchProjects} variant="outline" className="text-blue-400 border-blue-400 hover:bg-blue-400/10 hover:text-blue-300">
            Try Again
          </Button>
        </motion.div>
      )}

      {!isLoading && !error && projects.length === 0 && (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center py-10"
        >
          <Briefcase className="mx-auto h-16 w-16 text-slate-500 mb-4" />
          <h2 className="text-xl font-semibold text-slate-300 mb-2">No Projects Yet</h2>
          <p className="text-slate-400 mb-4">Get started by creating your first project.</p>
          <Button onClick={() => setIsDialogOpen(true)} variant="outline" className="text-blue-400 border-blue-400 hover:bg-blue-400/10 hover:text-blue-300">
            Create Your First Project
          </Button>
        </motion.div>
      )}

      {!isLoading && !error && projects.length > 0 && (
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          animate="visible"
          variants={{
            visible: { transition: { staggerChildren: 0.1 } }
          }}
        >
          {projects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </motion.div>
      )}

      <CreateProjectDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onSave={addProject}
      />
    </div>
  );
};

export default ProjectsPage;